import { Alert, Button, Callout, FormLabel, Modal, Switch, Toast, useToast } from "@oysterhr/ui"
import { useMemo, useState, useCallback } from "react"
import { useForm } from "react-hook-form"
import {
  AgreementAutomationStateEnum,
  AgreementPartyTypeEnum,
  AgreementStateEnum,
  Employment,
  EnvelopeStateEnum,
  EnvelopeTypeEnum,
} from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { useAgreementsGenerationWithPolling } from "../hooks/useAgreementsGenerationWithPolling"
import { useUnsupportedAutomation } from "./UnsupportedAutomationBanner/useUnsupportedAutomation"

interface ContractGenerationBannerProps {
  employment: Employment
}

const LIST_OF_AGREEMENT_STATE_FOR_REGENERATION = [
  AgreementStateEnum.Draft,
  AgreementStateEnum.Pending,
  AgreementStateEnum.Verified,
  AgreementStateEnum.ReadyToSign,
]

/**
 * Function to calculate if we need to show the ContractGeneration / ContractRegeneration banner
 */
const getBannerVisibility = (employment: Employment, agreementsAreBeingGenerated: boolean) => {
  const allAgreementsAreSigned = employment?.hiring?.agreements?.every(
    agreement => agreement.state === AgreementStateEnum.Signed && agreement.isESignEnabled,
  )

  if (allAgreementsAreSigned) {
    return false
  }

  if (!employment.hiring) {
    return false
  }

  if (agreementsAreBeingGenerated) {
    return false
  }

  if (!employment.isEditable) {
    return false
  }

  const pendingAgreementPresent = employment?.hiring?.agreements?.some(
    agreement => agreement.isAutomated && agreement.state === AgreementStateEnum.Pending,
  )

  if (pendingAgreementPresent) {
    return true
  }

  const agreementsCanBeRegenerated = employment?.hiring?.agreements?.some(
    agreement =>
      agreement.isAutomated && LIST_OF_AGREEMENT_STATE_FOR_REGENERATION.includes(agreement.state),
  )

  if (agreementsCanBeRegenerated) {
    return !employment.hiring.agreementsSynced
  }

  return false
}

export const ContractGenerationBanner = ({ employment }: ContractGenerationBannerProps) => {
  const [t] = useTranslation("Agreements")
  // Hooks
  const { generateAgreementsAsync } = useAgreementsGenerationWithPolling(employment.id)
  const { hideGenerationBanner } = useUnsupportedAutomation(employment)
  const toast = useToast()

  // Local State
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const someAgreementsGenerated = useMemo(
    () =>
      employment.hiring?.agreements?.some(
        agreement => agreement.isAutomated && agreement.state !== AgreementStateEnum.Pending,
      ) || false,
    [employment.hiring],
  )

  const agreementsAreBeingGenerated = useMemo(() => {
    if (employment?.hiring) {
      return employment.hiring.agreements.some(
        agreement => agreement.automation?.state === AgreementAutomationStateEnum.InProgress,
      )
    } else {
      return false
    }
  }, [employment.hiring])

  const generateAgreements = useCallback(
    async ({ sendNotifications }: { sendNotifications: boolean }) => {
      const res = await generateAgreementsAsync({
        variables: {
          input: {
            sendNotifications: sendNotifications,
            hiringId: employment.hiring?.id ?? "",
            actorParty: AgreementPartyTypeEnum.OysterStaff,
          },
        },
      })

      if (res.data?.generateAgreementsAsync?.errors?.length) {
        toast({
          render: (
            <Toast variation="error">
              <Toast.Content>{t("generationBanner.flashMessages.error")}</Toast.Content>
            </Toast>
          ),
        })
      }
      setShowConfirmationModal(false)
    },
    [generateAgreementsAsync, setShowConfirmationModal, t, toast, employment],
  )

  const showBanner = useMemo(
    () => getBannerVisibility(employment, agreementsAreBeingGenerated),
    [employment, agreementsAreBeingGenerated],
  )

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      sendNotifications: false,
    },
  })
  const sendNotifications = watch("sendNotifications")

  // The envelope is only sent if ESign is enabled, so there's no need to also see if ESign is enabled
  const envelopeWasSentForEmploymentAgreements = useMemo(() => {
    return (
      employment.hiring?.envelopes.some(
        envelope =>
          envelope.envelopeType === EnvelopeTypeEnum.EmploymentAgreements &&
          envelope.state === EnvelopeStateEnum.Sent,
      ) ?? false
    )
  }, [employment])

  if (hideGenerationBanner) return null

  return showBanner ? (
    <>
      <div className="mx-6 mt-10" data-testid="agremeent-generation-banner">
        <Callout>
          <Callout.Heading>
            {someAgreementsGenerated
              ? t("generationBanner.regeneration.title")
              : t("generationBanner.generation.title")}
          </Callout.Heading>
          <Callout.Content>
            {someAgreementsGenerated
              ? t("generationBanner.regeneration.description")
              : t("generationBanner.generation.description")}
          </Callout.Content>

          <Callout.Actions>
            <Callout.ActionButton
              variation="outline"
              onClick={() => setShowConfirmationModal(true)}
            >
              {someAgreementsGenerated
                ? t("generationBanner.regeneration.buttonLabel")
                : t("generationBanner.generation.buttonLabel")}
            </Callout.ActionButton>
          </Callout.Actions>
        </Callout>
      </div>

      <Modal
        rootId="modal"
        size="md"
        isOpen={showConfirmationModal}
        onRequestClose={() => setShowConfirmationModal(false)}
      >
        <Modal.Header>
          {someAgreementsGenerated
            ? t("generationBanner.confirmationModal.regeneration.title")
            : t("generationBanner.confirmationModal.generation.title")}
        </Modal.Header>
        <form onSubmit={handleSubmit(generateAgreements)}>
          <Modal.Content className="mb-5 flex flex-col space-y-4">
            {someAgreementsGenerated && envelopeWasSentForEmploymentAgreements && (
              <Alert variation="warning">
                <Alert.Content>
                  {t("generationBanner.confirmationModal.regeneration.eSign.warning")}
                </Alert.Content>
              </Alert>
            )}
            <span className="text-gray-400">
              {someAgreementsGenerated
                ? t("generationBanner.confirmationModal.regeneration.description")
                : t("generationBanner.confirmationModal.generation.description")}
            </span>

            <div className="flex items-center p-1">
              <Switch
                label={<FormLabel label="Send email notification to customer?" />}
                {...register("sendNotifications")}
                checked={sendNotifications}
              />
            </div>

            {sendNotifications && (
              <div className="text-s font-normal text-gray-400">
                {t("generationBanner.confirmationModal.generation.notificationInfo")}
              </div>
            )}

            {!someAgreementsGenerated && (
              <Alert>
                <Alert.Icon />
                <Alert.Content>
                  {t("generationBanner.confirmationModal.generation.alert")}
                </Alert.Content>
              </Alert>
            )}
          </Modal.Content>

          <Modal.Footer className="flex flex-row items-center justify-end space-x-4">
            <Button variation="outline" onClick={() => setShowConfirmationModal(false)}>
              Cancel
            </Button>
            <Button variation="primary" type="submit">
              {t("generationBanner.confirmationModal.buttonLabel")}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  ) : null
}
